import { AppModal } from "./AppModal";
import { useCallback, useEffect, useRef, useState } from "react";
import { GeoJSONSource, LngLatBounds, Map as MLMap, NavigationControl } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { apiGet, apiSendVerbatim } from "../api/client";
import { useAppData } from "../state/AppData";
import { buildOsmStyle, MAP_CONTAINER_BACKGROUND } from "../lib/mapStyle";
import {
  addPoint,
  buildSettingsPatch,
  candidateLabel,
  canSaveCustom,
  clearPoints,
  createPayload,
  draftFromRow,
  fillFromCandidate,
  isEmptyPatch,
  normalizeCodeInput,
  pointsFromCandidate,
  previewGeoJSON,
  rowDisplayName,
  saveBlockedReason,
  sourceLabel,
  undoPoint,
  updatePayload,
  type ImportCandidate,
  type LatLng,
  type MeshRegionRow,
  type MeshRegionsResponse,
  type RegionDraft,
} from "../lib/regionManager";

// Admin region manager: the "+ Add region" button (RF Health toolbar and the
// Settings list), the Custom dialog, and the region list
// itself. Logic that decides anything (payloads, patches, polygon rules)
// lives in lib/regionManager.ts; this file is wiring and layout.

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <AppModal
      title={title}
      onClose={onClose}
      headExtra={
        <button type="button" className="rf-btn rf-btn-outline" data-cancel onClick={onClose}>
          Close
        </button>
      }
    >
      {children}
    </AppModal>
  );
}

function ErrorLine({ text }: { text: string | null }) {
  return text ? (
    <div className="rf-error" role="alert">
      {text}
    </div>
  ) : null;
}

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

// --- Custom region dialog (hand-rolled maplibre polygon draw) -----------------------

const DRAFT_SOURCE = "rf-draft";

function emptyFc(): GeoJSON.FeatureCollection {
  return { type: "FeatureCollection", features: [] };
}

function draftFc(points: LatLng[]): GeoJSON.FeatureCollection {
  const g = previewGeoJSON(points);
  const features: GeoJSON.Feature[] = [];
  if (g.ring) features.push({ type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [g.ring] } });
  if (g.line) features.push({ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: g.line } });
  for (const p of g.points) features.push({ type: "Feature", properties: {}, geometry: { type: "Point", coordinates: p } });
  return { type: "FeatureCollection", features };
}

function fitTo(map: MLMap, rings: LatLng[][]) {
  const b = new LngLatBounds();
  let any = false;
  for (const ring of rings) {
    for (const p of ring) {
      b.extend([p[1], p[0]]);
      any = true;
    }
  }
  if (any) map.fitBounds(b, { padding: 40, maxZoom: 12, duration: 0 });
}

export function CustomRegionDialog({
  onClose,
  onSaved,
  editing,
}: {
  onClose: () => void;
  onSaved?: () => void;
  editing?: MeshRegionRow;
}) {
  const { effectiveTheme } = useAppData();
  const [code, setCode] = useState(editing?.code ?? "");
  const [name, setName] = useState(editing?.name ?? "");
  const [points, setPoints] = useState<LatLng[]>(() => (editing ? editing.polygon.map((p) => [p[0], p[1]] as LatLng) : []));
  const [candidates, setCandidates] = useState<ImportCandidate[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MLMap | null>(null);
  const readyRef = useRef(false);
  const pointsRef = useRef<LatLng[]>(points);
  pointsRef.current = points;
  const codeRef = useRef(code);
  codeRef.current = code;
  const nameRef = useRef(name);
  nameRef.current = name;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const map = new MLMap({
      container: el,
      style: buildOsmStyle(effectiveTheme),
      center: [0, 20],
      zoom: 1.5,
      attributionControl: { compact: true },
    });
    mapRef.current = map;
    map.addControl(new NavigationControl({ showCompass: false }), "top-right");
    map.on("load", () => {
      map.addSource(DRAFT_SOURCE, { type: "geojson", data: draftFc(pointsRef.current) });
      map.addLayer({ id: "rf-draft-fill", type: "fill", source: DRAFT_SOURCE, filter: ["==", "$type", "Polygon"], paint: { "fill-color": "#2a78d6", "fill-opacity": 0.2 } });
      map.addLayer({ id: "rf-draft-line", type: "line", source: DRAFT_SOURCE, filter: ["==", "$type", "LineString"], paint: { "line-color": "#2a78d6", "line-width": 2 } });
      map.addLayer({
        id: "rf-draft-points",
        type: "circle",
        source: DRAFT_SOURCE,
        filter: ["==", "$type", "Point"],
        paint: { "circle-radius": 5, "circle-color": "#ffffff", "circle-stroke-color": "#2a78d6", "circle-stroke-width": 2 },
      });
      readyRef.current = true;
      if (pointsRef.current.length > 0) fitTo(map, [pointsRef.current]);
    });
    map.on("click", (e) => {
      setPoints((prev) => addPoint(prev, [e.lngLat.lat, e.lngLat.lng]));
    });
    // New region: start framed on the regions already there, if any.
    if (!editing) {
      apiGet<MeshRegionsResponse>("/api/regions/mesh")
        .then((r) => {
          if (pointsRef.current.length === 0 && mapRef.current) fitTo(mapRef.current, r.regions.map((x) => x.polygon as LatLng[]));
        })
        .catch(() => undefined);
    }
    return () => {
      readyRef.current = false;
      map.remove();
      mapRef.current = null;
    };
    // The map is built once per dialog open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !readyRef.current) return;
    const src = map.getSource(DRAFT_SOURCE) as GeoJSONSource | undefined;
    src?.setData(draftFc(points));
  }, [points]);

  async function onFile(file: File | undefined) {
    if (!file) return;
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file, file.name);
      const res = await apiSendVerbatim<{ candidates: ImportCandidate[] }>("POST", "/api/regions/mesh/import", form);
      const found = res?.candidates ?? [];
      setCandidates(found);
      if (found.length === 1) pick(found[0]);
    } catch (e) {
      setCandidates(null);
      setError(errText(e));
    }
  }

  function pick(c: ImportCandidate) {
    const pts = pointsFromCandidate(c);
    setPoints(pts);
    if (!editing) {
      const f = fillFromCandidate({ code: codeRef.current, name: nameRef.current }, c);
      setCode(f.code);
      setName(f.name);
    }
    if (mapRef.current) fitTo(mapRef.current, [pts]);
  }

  const saveState = { code, name, points, codeLocked: !!editing };
  const canSave = canSaveCustom(saveState);
  const blockedReason = saveBlockedReason(saveState);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      if (editing) {
        await apiSendVerbatim("PUT", `/api/regions/mesh/custom/${encodeURIComponent(editing.code)}`, updatePayload(name, points));
      } else {
        await apiSendVerbatim("POST", "/api/regions/mesh/custom", createPayload(code, name, points));
      }
      onSaved?.();
      onClose();
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={editing ? "Edit shape" : "Add custom region"} onClose={onClose}>
      <div className="rf-fields">
        <label className="settings-field">
          <span>Code</span>
          <input type="text" value={code} disabled={!!editing} onChange={(e) => setCode(normalizeCodeInput(e.target.value))} />
        </label>
        <label className="settings-field">
          <span>Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
      </div>
      <div className="rf-map-tools">
        <button type="button" className="rf-btn rf-btn-outline" disabled={points.length === 0} onClick={() => setPoints((p) => undoPoint(p))}>
          Undo point
        </button>
        <button type="button" className="rf-btn rf-btn-outline" disabled={points.length === 0} onClick={() => setPoints(clearPoints())}>
          Clear
        </button>
        <span className="rf-sub">Points: {points.length}</span>
        <label className="rf-file">
          Import file (.geojson, .json, .kml){" "}
          <input type="file" accept=".geojson,.json,.kml" onChange={(e) => onFile(e.target.files?.[0])} />
        </label>
      </div>
      {candidates && candidates.length > 1 && <div className="rf-sub">The file has {candidates.length} shapes. Click one to load it.</div>}
      {candidates && (
        <ul className="rf-candidates">
          {candidates.length === 0 && <li>{"—"}</li>}
          {candidates.map((c, i) => (
            <li key={`${c.name}-${i}`}>
              <button type="button" className="rf-btn rf-btn-outline" onClick={() => pick(c)}>
                {candidateLabel(c)}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div ref={containerRef} className="rf-map" style={{ background: MAP_CONTAINER_BACKGROUND[effectiveTheme] }} />
      <div className="rf-sub rf-map-hint">Click the map to add points (at least 3). Fill Code and Name to save.</div>
      <ErrorLine text={error} />
      <div className="rf-modal-actions">
        {blockedReason && <span className="rf-sub" role="status">{blockedReason}</span>}
        <button
          type="button"
          className={`rf-btn ${canSave ? "rf-btn-primary" : "rf-btn-quiet"}`}
          disabled={busy || !canSave}
          title={blockedReason ?? undefined}
          onClick={save}
        >
          Save
        </button>
      </div>
    </Modal>
  );
}

// --- "+ Add region" menu --------------------------------------------------------------

export function AddRegionMenu({ onSaved }: { onSaved?: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rf-add-wrap">
      <button type="button" className="settings-save-btn" onClick={() => setOpen(true)}>
        + Add region
      </button>
      {open && <CustomRegionDialog onClose={() => setOpen(false)} onSaved={onSaved} />}
    </div>
  );
}

// --- region list -------------------------------------------------------------------------

function RegionRowEditor({ row, onChanged }: { row: MeshRegionRow; onChanged: () => void }) {
  const [draft, setDraft] = useState<RegionDraft>(() => draftFromRow(row));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editShape, setEditShape] = useState(false);
  const patch = buildSettingsPatch(row, draft);
  const dirty = !isEmptyPatch(patch);
  const custom = row.source === "custom";

  // The stored row changed (after a save or reload): the draft restarts from it.
  const rowSig = JSON.stringify([row.enabled, row.display_name]);
  useEffect(() => {
    setDraft(draftFromRow(row));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSig]);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await apiSendVerbatim("PATCH", `/api/regions/mesh/${encodeURIComponent(row.code)}/settings`, patch);
      onChanged();
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    const what = custom ? "Delete" : "Remove";
    if (!window.confirm(`${what} ${rowDisplayName(row)} (${row.code})?`)) return;
    setBusy(true);
    setError(null);
    try {
      await apiSendVerbatim("DELETE", custom ? `/api/regions/mesh/custom/${encodeURIComponent(row.code)}` : `/api/regions/mesh/meshmapper/${encodeURIComponent(row.code)}`);
      onChanged();
    } catch (e) {
      setError(errText(e));
      setBusy(false);
    }
  }

  return (
    <>
      <tr>
        <td>{rowDisplayName(row)}</td>
        <td>{row.code}</td>
        <td>{sourceLabel(row.source, row.frozen === true)}</td>
        <td>
          <input type="checkbox" aria-label={`Enabled ${row.code}`} checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
        </td>
        <td>
          <input type="text" aria-label={`Display name ${row.code}`} placeholder={row.name} value={draft.displayName} onChange={(e) => setDraft({ ...draft, displayName: e.target.value })} />
        </td>
        <td className="rf-row-actions">
          <button
            type="button"
            className={`rf-btn ${dirty ? "rf-btn-primary" : "rf-btn-quiet"}`}
            disabled={!dirty || busy}
            title={dirty ? undefined : "No unsaved changes"}
            onClick={save}
          >
            Save
          </button>
          {custom && (
            <button type="button" className="rf-btn rf-btn-outline" disabled={busy} onClick={() => setEditShape(true)}>
              Edit shape
            </button>
          )}
          <button type="button" className="rf-link-btn rf-remove-btn" disabled={busy} onClick={remove}>
            {custom ? "Delete" : "Remove"}
          </button>
        </td>
      </tr>
      {error && (
        <tr>
          <td colSpan={6}>
            <ErrorLine text={error} />
          </td>
        </tr>
      )}
      {editShape && <CustomRegionDialog editing={row} onClose={() => setEditShape(false)} onSaved={onChanged} />}
    </>
  );
}

export function MeshRegionsManager() {
  const [data, setData] = useState<MeshRegionsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    apiGet<MeshRegionsResponse>("/api/regions/mesh")
      .then((r) => {
        setData(r);
        setError(null);
      })
      .catch((e) => setError(errText(e)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="rf-manager">
      <div className="node-list-toolbar">
        <AddRegionMenu onSaved={load} />
      </div>
      <ErrorLine text={error} />
      {data === null && !error && <div className="empty-state">Loading…</div>}
      {data && (
        <div className="node-list-table-wrap">
          <table className="node-list-table rf-manager-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Source</th>
                <th>Enabled</th>
                <th>Display name</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.regions.map((r) => (
                <RegionRowEditor key={r.code} row={r} onChanged={load} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="settings-field-desc">Changes show on RF Health after the next recalculation (up to 15 minutes).</div>
    </div>
  );
}
